/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.3
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useMemo } from "react";
import PropTypes from "prop-types";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

import SoftTypography from "components/SoftTypography";
import SoftBox from "components/SoftBox";
import SalesTableCell from "examples/Tables/SalesTable/SalesTableCell";

function SalesTable({ title, rows = [{}], footerData = null, compact = false }) {
  const renderTableCells = rows.map((row, key) => {
    const tableRows = [];
    const rowKey = `row-${key}`;

    Object.entries(row).map(([cellTitle, cellContent]) =>
      Array.isArray(cellContent)
        ? tableRows.push(
            <SalesTableCell
              key={`cell-${cellTitle}`}
              title={cellTitle}
              content={cellContent[1]}
              image={cellContent[0]}
              noBorder={false}
              compact={compact}
            />
          )
        : tableRows.push(
            <SalesTableCell
              key={`cell-${cellTitle}`}
              title={cellTitle}
              content={cellContent}
              noBorder={false}
              compact={compact}
            />
          )
    );

    return <TableRow key={rowKey}>{tableRows}</TableRow>;
  });

  const footerCells = footerData
    ? [
        ["Grand Total", ""],
        ...(footerData.total_completes !== undefined
          ? [["Completes", new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(footerData.total_completes)]]
          : []),
        ...(footerData.total_revenue !== undefined
          ? [["Revenue", new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(footerData.total_revenue)]]
          : []),
        ["Percentage", `${Number(footerData.total_percentage ?? 100).toFixed(2)}%`],
      ]
    : [];

  const renderFooter = footerData ? (
    <TableRow sx={{ backgroundColor: "#e0f2fe",
        position: "sticky",
            bottom: 0,
            width: "100%",
     }}>
      {footerCells.map(([title, content]) => (
        <SalesTableCell key={title} title={title} content={content} compact={compact} />
      ))}
    </TableRow>
  ) : null;

  return (
    <SoftBox sx={{ position: "relative", width: "100%" }}>
      <TableContainer
        sx={{
          maxHeight: "400px",
          overflowY: "auto",
          "&::-webkit-scrollbar": { display: "none" },
          scrollbarWidth: "none",
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <SoftTypography
                variant="h6"
                component="th"
                sx={
                  compact
                    ? {
                        fontSize: "12px !important",
                        fontWeight: "400 !important",
                        lineHeight: "1.15 !important",
                        textTransform: "capitalize",
                      }
                    : undefined
                }
              >
                {title}
              </SoftTypography>
            </TableRow>
          </TableHead>
          <TableBody>
            {useMemo(() => renderTableCells, [rows])}
          </TableBody>
          {/* Footer inside same table to align columns */}
          {footerData && <TableBody>{renderFooter}</TableBody>}
        </Table>
      </TableContainer>
    </SoftBox>
  );
}

SalesTable.propTypes = {
  title: PropTypes.string.isRequired,
  rows: PropTypes.arrayOf(PropTypes.object),
  footerData: PropTypes.object,
  compact: PropTypes.bool,
};

export default SalesTable;
